import React from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  Box,
  SxProps,
  Theme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useSearchBox } from "react-instantsearch";
import { searchBoxStyles } from "../../pages/styles";

/**
 * Props interface for the SearchBox component.
 *
 * @interface SearchBoxProps
 */
export interface SearchBoxProps {
  /** Placeholder text for the search input field */
  placeholder?: string;
  /** Additional styles to apply to the container Box component */
  sx?: SxProps<Theme>;
  /** Whether the input should have a full width */
  fullWidth?: boolean;
  /** Size variant of the TextField component */
  size?: "small" | "medium";
  /** Visual variant of the TextField component */
  variant?: "outlined" | "filled" | "standard";
}

/**
 * Custom SearchBox component that integrates Material-UI with react-instantsearch.
 *
 * This component provides a search input field with real-time search capabilities,
 * visual feedback for search state, and a clear button when there's input.
 *
 * Features:
 * - Real-time search as user types
 * - Visual search icon that changes appearance during search
 * - Clear button that appears when there's input
 * - Keyboard support (Enter key handling)
 * - Customizable styling and appearance
 * - Integration with react-instantsearch for search functionality
 *
 * @param props - The component props
 * @param props.placeholder - Placeholder text for the search input (default: "Search...")
 * @param props.sx - Additional styles for the container
 * @param props.fullWidth - Whether the input should be full width (default: true)
 * @param props.size - Size variant of the TextField (default: "small")
 * @param props.variant - Visual variant of the TextField (default: "outlined")
 * @returns {JSX.Element} The search box component
 */
const SearchBox: React.FC<SearchBoxProps> = ({
  placeholder = "Search...",
  sx = {},
  fullWidth = true,
  size = "small",
  variant = "outlined",
}) => {
  const { query, refine, clear, isSearchStalled } = useSearchBox();

  /**
   * Handles input change events and triggers search refinement.
   *
   * @param event - The input change event
   */
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    refine(event.target.value);
  };

  /**
   * Clears the search input and resets the search results.
   */
  const handleClear = () => {
    clear();
  };

  /**
   * Handles keyboard events for the search input.
   * Currently handles Enter key to prevent form submission.
   *
   * @param event - The keyboard event
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      // The search is already triggered by refine() in handleInputChange
    }
  };

  return (
    <Box sx={sx}>
      <TextField
        fullWidth={fullWidth}
        size={size}
        variant={variant}
        placeholder={placeholder}
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                fontSize="small"
                sx={
                  isSearchStalled
                    ? searchBoxStyles.searchIconStalled
                    : searchBoxStyles.searchIcon
                }
              />
            </InputAdornment>
          ),
          endAdornment: query && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClear}
                sx={searchBoxStyles.clearButton}
                aria-label="Clear search"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={searchBoxStyles.textField}
      />
    </Box>
  );
};

export default SearchBox;
